export interface UserScopedPermission {
  id: string
  name: string
  storeId: string
  descriptionMd?: string
  notesMd?: string
  namespace?: string
  pathType?: "exact" | "prefix"
  path?: string
  method?: string
  policy: "allow" | "deny"
}

export interface UserPermissions {
  userId: string
  permissions: UserScopedPermission[]
}

/**
 * Checks if a user is authorized for a specific permission and scope.
 * @param userId - The user identifier (email)
 * @param permissionName - The permission name (e.g., 'store.read', 'store.create')
 * @param scope - The scope object containing storeId ('*' for a global permission check)
 * @param userPermissions - The user's cached permissions object
 * @returns True if authorized, false otherwise
 */
export function isAuthorized(
  userId: string,
  permissionName: string,
  scope: { storeId: string },
  userPermissions: UserPermissions | null | undefined
): boolean {
  if (!userPermissions || userPermissions.permissions.length === 0) {
    return false
  }

  // Double-check that the userId matches the userId in the userPermissions structure
  if (userPermissions.userId !== userId) {
    console.warn(`isAuthorized: userId mismatch: expected ${userId}, got ${userPermissions.userId}`)
    return false
  }

  const { storeId } = scope

  return userPermissions.permissions.some((permission) => {
    // Match permission name
    if (permission.name !== permissionName) {
      return false
    }

    // Check store scope - permission.storeId can be a specific store or '*' for global
    if (permission.storeId === storeId || permission.storeId === "*") {
      // Default policy is 'allow', so check if it's explicitly denied
      return permission.policy !== "deny"
    }

    return false
  })
}

export interface SessionLike {
  userId: string | null
  permissions: UserPermissions | null
  isLoading: boolean
}

export interface AuthorizationState {
  authorized: boolean
  isLoading: boolean
}

export function checkAuthorization(
  session: SessionLike,
  permissionName: string,
  storeId: string
): AuthorizationState {
  if (session.isLoading) {
    return { authorized: false, isLoading: true }
  }

  return {
    authorized:
      Boolean(session.userId) &&
      isAuthorized(session.userId as string, permissionName, { storeId }, session.permissions),
    isLoading: false,
  }
}
